import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Table, Tooltip } from "antd";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { Link, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import IntlCurrencyInput from "react-intl-currency-input";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import CancelFreightDialog from "../frete-copy/components/CancelFreightDialog";
import Utils from "../../util/utils";
import {
  cancelFreightById,
  canCancelFreightStatus,
  getCancelFreightErrorMessage,
  resolveFreightStatusCode,
} from "../../util/freight/cancelFreight";
import {
  acceptTransportQueueHubCandidate,
  counterTransportQueueHubCandidate,
  fetchTransportQueueHubSummary,
  rejectTransportQueueHubCandidate,
} from "../../service/transportQueueHub.api";
import { getAcceptTransportErrorMessage } from "../../components/notifications/acceptTransportFromQueue";
import {
  buildHubQueryParams,
  EMPTY_QUEUE_HUB_FILTERS,
  enrichFreightGroupsWithCarrierProfiles,
  filterFreightGroupsByRoute,
  formatCandidateStatus,
  formatCarrierType,
  formatCurrency,
  formatDateBr,
  formatDocumentationStatus,
  formatNullable,
  formatWaitingTime,
  getHubPaginationTotal,
  HUB_TABS,
  mapShippersFromHub,
  mapTransportQueueHubFreights,
} from "./transportQueueHub.mapper";
import { markTransportQueueNotificationsRead } from "../../components/notifications/transportQueueNotification.utils";
import shipperService from "../../service/shipper.service";
import "./filas-transportadoras.css";

function positiveMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function formatProposalLabel(total, unit) {
  const money = formatCurrency(total);
  const unitAmount = positiveMoney(unit);
  if (!unitAmount) return money;
  return `${money} (${formatCurrency(unitAmount)}/Ton)`;
}

function ShipperAmount({ total, unit }) {
  const unitAmount = positiveMoney(unit);
  return (
    <Box>
      <Typography className="filas-hub-value">{formatCurrency(total)}</Typography>
      {unitAmount ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {formatCurrency(unitAmount)}/Ton
        </Typography>
      ) : null}
    </Box>
  );
}

const acceptButtonSx = {
  bgcolor: "#012442",
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: "#01365f", boxShadow: "none" },
};
const outlineButtonSx = {
  color: "#012442",
  borderColor: "rgba(1, 36, 66, 0.28)",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#012442",
    backgroundColor: "rgba(1, 36, 66, 0.04)",
  },
};
const cancelButtonSx = {
  color: "#fff",
  bgcolor: "#ff3100",
  borderColor: "#ff3100",
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#e02c00",
    borderColor: "#e02c00",
    boxShadow: "none",
  },
};

const rejectOutlineButtonSx = {
  color: "#b91c1c",
  borderColor: "rgba(185, 28, 28, 0.35)",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    borderColor: "#b91c1c",
    backgroundColor: "rgba(185, 28, 28, 0.06)",
  },
};

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

const SUMMARY_CARDS = [
  {
    key: "openFreights",
    label: "Em aberto",
    hint: "Fretes aguardando propostas",
    legacyKey: "freightsWithQueue",
    tone: "orange",
    Icon: FolderOpenOutlinedIcon,
  },
  {
    key: "totalCandidates",
    label: "Propostas recebidas",
    hint: "Em todos os fretes do escopo",
    tone: "blue",
    Icon: DescriptionOutlinedIcon,
  },
  {
    key: "acceptedLast30Days",
    label: "Aceitos",
    hint: "Últimos 30 dias",
    tone: "green",
    Icon: TaskAltOutlinedIcon,
  },
  {
    key: "awaitingResponse",
    label: "Aguardando resposta",
    hint: "Transportadoras na fila",
    tone: "amber",
    Icon: HourglassEmptyOutlinedIcon,
  },
];

const EMPTY_TEXT_BY_TAB = {
  open: "Nenhum frete em aberto para os filtros aplicados.",
  accepted: "Nenhum frete aceito para os filtros aplicados.",
  finished: "Nenhum frete finalizado para os filtros aplicados.",
  cancelled: "Nenhum frete cancelado para os filtros aplicados.",
};

function canShowCancelForFreight(freight, user) {
  const canEdit = Boolean(user?.permissoes?.freight?.isEdit || user?.viewALL);
  if (!canEdit) return false;

  const statusCode = resolveFreightStatusCode(freight?.status);
  return statusCode !== "08" && canCancelFreightStatus(statusCode);
}

function resolveStatusTone(uiLabel, tab) {
  const text = String(uiLabel || "").toLowerCase();
  if (tab === "cancelled" || text.includes("cancel")) return "red";
  if (tab === "finished" || text.includes("final")) return "gray";
  if (tab === "accepted" || text.includes("aceit") || text.includes("contrat")) {
    return "green";
  }
  if (text.includes("análise") || text.includes("analise")) return "green";
  if (text.includes("recebendo") || text.includes("proposta")) return "orange";
  return "blue";
}

function StatusBadge({ label, tone = "blue" }) {
  return (
    <span className={`filas-hub-status filas-hub-status--${tone}`}>
      <span className="filas-hub-status__dot" />
      {label || "—"}
    </span>
  );
}

function ProposalCountBadge({ value }) {
  return <span className="filas-hub-proposals-badge">{value ?? 0}</span>;
}

function CandidateStatusChip({ status, label }) {
  const display = formatCandidateStatus(status, label);
  const tone =
    status === "accepted"
      ? "green"
      : status === "rejected"
        ? "red"
        : status === "in_review"
          ? "amber"
          : "blue";
  return <span className={`filas-hub-chip filas-hub-chip--${tone}`}>{display}</span>;
}

function extractCityUf(value) {
  const match = String(value || "").match(/^(.*?)[,-/]\s*([A-Za-z]{2})$/);
  if (!match) return null;
  return {
    city: match[1].trim(),
    uf: match[2].trim().toUpperCase(),
  };
}

function buildCityOptions(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];

  const parsed = extractCityUf(raw);
  const result =
    parsed?.city && parsed?.uf
      ? Utils.buscarMunicipioPorNomeEUF(parsed.city, parsed.uf)
      : Utils.buscarMunicipiosPorNome(raw);

  const uniqueNames = Array.from(
    new Set(
      (result || [])
        .map((item) => {
          const city = item?.xMunCarrega;
          const uf = item?.UF;
          if (!city) return null;
          return uf ? `${city} - ${uf}` : city;
        })
        .filter(Boolean),
    ),
  );

  return uniqueNames.slice(0, 30);
}

export default function FilasTransportadorasPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);

  const [filters, setFilters] = useState({ ...EMPTY_QUEUE_HUB_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_QUEUE_HUB_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [shippers, setShippers] = useState([]);
  const [paginationTotal, setPaginationTotal] = useState(0);
  const [freightGroups, setFreightGroups] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [acceptingKey, setAcceptingKey] = useState(null);
  const [confirmAccept, setConfirmAccept] = useState(null);
  const [confirmCounter, setConfirmCounter] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);
  const [counterAmount, setCounterAmount] = useState(0);
  const [cancelFreightLoading, setCancelFreightLoading] = useState(false);
  const [openCancelFreightDialog, setOpenCancelFreightDialog] = useState(false);
  const [pendingCancelFreight, setPendingCancelFreight] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);

  const canView =
    user?.permissoes?.filasTransportadoras?.isView ||
    user?.viewALL;

  const activeTab = appliedFilters.tab || "open";
  const canAcceptOnTab = activeTab === "open";

  const loadHub = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = buildHubQueryParams(appliedFilters, user);
      const data = await fetchTransportQueueHubSummary(params);
      setSummary(data.summary ?? null);
      setShippers(mapShippersFromHub(data));
      const groups = mapTransportQueueHubFreights(data);

      const carrierIds = [
        ...new Set(
          groups.flatMap((freight) =>
            (freight.queue || [])
              .map((candidate) => candidate.idCarrier)
              .filter(Boolean),
          ),
        ),
      ];

      const profilesById = {};
      await Promise.all(
        carrierIds.map(async (carrierId) => {
          try {
            const snap = await shipperService.getShipperAvailable(carrierId);
            if (snap?.exists) {
              profilesById[carrierId] = { id: snap.id, ...snap.data() };
            }
          } catch (profileErr) {
            console.warn(
              "[FilasTransportadoras] Falha ao carregar cadastro:",
              carrierId,
              profileErr,
            );
          }
        }),
      );

      const enrichedGroups = enrichFreightGroupsWithCarrierProfiles(
        groups,
        profilesById,
      );
      const routeFilteredGroups = filterFreightGroupsByRoute(
        enrichedGroups,
        appliedFilters,
      );
      setFreightGroups(routeFilteredGroups);
      setPaginationTotal(
        routeFilteredGroups.length !== enrichedGroups.length
          ? routeFilteredGroups.length
          : getHubPaginationTotal(data, enrichedGroups.length),
      );
      setExpandedRowKeys([]);

      const readEntries = routeFilteredGroups.flatMap((freight) =>
        (freight.queue || []).map((candidate) => ({
          freightId: freight.freightId,
          idTransport: candidate.idCarrier,
        })),
      );
      markTransportQueueNotificationsRead(readEntries);
    } catch (err) {
      console.error("[FilasTransportadoras] Erro ao carregar central:", err);
      setError("Não foi possível carregar a gestão de lances.");
      setSummary(null);
      setShippers([]);
      setFreightGroups([]);
      setPaginationTotal(0);
      setExpandedRowKeys([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, user]);

  useEffect(() => {
    if (isLoggedIn && canView) {
      loadHub();
    }
  }, [isLoggedIn, canView, loadHub]);

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClear = () => {
    const tab = filters.tab || "open";
    const next = {
      ...EMPTY_QUEUE_HUB_FILTERS,
      tab,
      onlyWithQueue: false,
    };
    setFilters(next);
    setAppliedFilters(next);
    setOriginOptions([]);
    setDestinationOptions([]);
  };

  const handleTabChange = (_event, nextTab) => {
    const next = {
      ...filters,
      tab: nextTab,
      onlyWithQueue: false,
    };
    setFilters(next);
    setAppliedFilters(next);
  };

  const refreshAfterAccept = useCallback(async () => {
    await loadHub();
  }, [loadHub]);

  const handleAcceptCandidate = (freight, candidate) => {
    setConfirmAccept({
      ...freight,
      ...candidate,
      rowKey: candidate.rowKey,
    });
  };

  const handleConfirmAccept = async () => {
    if (!confirmAccept || acceptingKey) return;

    setAcceptingKey(confirmAccept.rowKey);

    try {
      const result = await acceptTransportQueueHubCandidate({
        freightId: confirmAccept.freightId,
        idCarrier: confirmAccept.idCarrier,
        nameCarrier: confirmAccept.nameCarrier,
        type: confirmAccept.type,
        idUser: user?.uid,
        viewAll: Boolean(user?.viewALL),
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: result?.message || "Transportadora aceita com sucesso.",
      });
      setConfirmAccept(null);
      await refreshAfterAccept();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getAcceptTransportErrorMessage(
          err,
          "Não foi possível aceitar a transportadora.",
        ),
      });
    } finally {
      setAcceptingKey(null);
    }
  };

  const handleOpenCounter = (freight, candidate) => {
    setConfirmCounter({
      ...freight,
      ...candidate,
      rowKey: candidate.rowKey,
    });
    setCounterAmount(Number(candidate.proposedAmount) || 0);
  };

  const handleConfirmCounter = async () => {
    if (!confirmCounter || acceptingKey) return;
    const amount = Number(counterAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Informe um valor de contraproposta.",
      });
      return;
    }

    setAcceptingKey(confirmCounter.rowKey);
    try {
      await counterTransportQueueHubCandidate({
        freightId: confirmCounter.freightId,
        idCarrier: confirmCounter.idCarrier,
        nameCarrier: confirmCounter.nameCarrier,
        idUser: user?.uid,
        amount,
        viewAll: Boolean(user?.viewALL),
      });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Contraproposta enviada à transportadora.",
      });
      setConfirmCounter(null);
      await refreshAfterAccept();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getAcceptTransportErrorMessage(
          err,
          "Não foi possível enviar a contraproposta.",
        ),
      });
    } finally {
      setAcceptingKey(null);
    }
  };

  const handleOpenReject = (freight, candidate) => {
    setConfirmReject({
      ...freight,
      ...candidate,
      rowKey: candidate.rowKey,
    });
  };

  const handleConfirmReject = async () => {
    if (!confirmReject || acceptingKey) return;
    setAcceptingKey(confirmReject.rowKey);
    try {
      await rejectTransportQueueHubCandidate({
        freightId: confirmReject.freightId,
        idCarrier: confirmReject.idCarrier,
        nameCarrier: confirmReject.nameCarrier,
        idUser: user?.uid,
        viewAll: Boolean(user?.viewALL),
      });
      setSnackbar({
        open: true,
        severity: "success",
        message: "Lance recusado. Este prestador saiu do frete.",
      });
      setConfirmReject(null);
      await refreshAfterAccept();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getAcceptTransportErrorMessage(
          err,
          "Não foi possível recusar este lance.",
        ),
      });
    } finally {
      setAcceptingKey(null);
    }
  };

  const toggleExpandFreight = (freightId) => {
    setExpandedRowKeys((prev) =>
      prev.includes(freightId)
        ? prev.filter((id) => id !== freightId)
        : [...prev, freightId],
    );
  };

  const handleOpenCancelFreightDialog = (freight) => {
    setPendingCancelFreight(freight);
    setOpenCancelFreightDialog(true);
  };

  const handleCloseCancelFreightDialog = () => {
    if (cancelFreightLoading) return;
    setOpenCancelFreightDialog(false);
    setPendingCancelFreight(null);
  };

  const handleConfirmCancelFreight = async (reason) => {
    const freightId = pendingCancelFreight?.freightId;
    if (!freightId || cancelFreightLoading) return;

    setCancelFreightLoading(true);

    try {
      await cancelFreightById(freightId, reason, {
        idUser: user?.uid,
        viewAll: Boolean(user?.viewALL),
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Frete cancelado com sucesso.",
      });
      handleCloseCancelFreightDialog();
      await loadHub();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getCancelFreightErrorMessage(
          err,
          "Não foi possível cancelar o frete.",
        ),
      });
    } finally {
      setCancelFreightLoading(false);
    }
  };

  const buildCandidateColumns = useCallback(
    (freight) => [
      {
        title: "Prestador",
        key: "nameCarrier",
        render: (_, candidate, index) => (
          <Box>
            <Box className="filas-hub-provider">
              <Typography sx={{ fontWeight: 700 }}>{candidate.nameCarrier}</Typography>
              {index === 0 && canAcceptOnTab ? (
                <span className="filas-hub-best-badge">
                  {freight.contractingType === "BID"
                    ? "Melhor lance"
                    : "Primeiro na fila"}
                </span>
              ) : null}
            </Box>
            {candidate.cnpj ? (
              <Typography variant="caption" color="text.secondary">
                CNPJ: {candidate.cnpj}
              </Typography>
            ) : null}
            {candidate.registeredAt ? (
              <Typography variant="caption" display="block" color="text.secondary">
                {freight.contractingType === "BID" ? "Lance enviado" : "Na fila"}{" "}
                {formatWaitingTime(candidate.registeredAt)}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        title: "Tipo",
        key: "type",
        width: 130,
        render: (_, candidate) => formatCarrierType(candidate.type),
      },
      {
        title: "Avaliação",
        key: "rating",
        width: 150,
        render: (_, candidate) => {
          const rating =
            typeof candidate.rating === "number" ? candidate.rating : 2.5;
          return (
            <Box className="filas-hub-rating">
              <Rating
                value={rating}
                precision={0.5}
                readOnly
                size="small"
                sx={{ color: "#f5a623" }}
              />
              <Typography variant="caption">{rating.toFixed(1)}</Typography>
            </Box>
          );
        },
      },
      {
        title: "Prazo de recebimento",
        key: "paymentTerm",
        width: 160,
        render: (_, candidate) =>
          formatNullable(
            freight.paymentTermLabel || candidate.paymentTermLabel,
          ),
      },
      {
        title: "Documentação",
        key: "documentationStatus",
        width: 130,
        render: (_, candidate) => {
          const label = formatDocumentationStatus(
            candidate.documentationStatus || "ok",
          );
          const tone =
            label === "Completa"
              ? "green"
              : label === "Incompleta"
                ? "red"
                : "amber";
          return <span className={`filas-hub-chip filas-hub-chip--${tone}`}>{label}</span>;
        },
      },
      {
        title: "Valor da proposta",
        key: "proposedAmount",
        width: 170,
        render: (_, candidate) => {
          if (freight.contractingType !== "BID") return formatNullable(null);
          const unit =
            freight.pricingType === "Ton" ? candidate.unitAmount : null;
          return (
            <Box>
              <ShipperAmount total={candidate.proposedAmount} unit={unit} />
              {candidate.counterAmount ? (
                <Typography variant="caption" color="text.secondary">
                  Contra: {formatCurrency(candidate.counterAmount)}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        title: "Status",
        key: "candidateStatus",
        width: 150,
        render: (_, candidate) => (
          <CandidateStatusChip
            status={candidate.candidateStatus}
            label={candidate.candidateStatusLabel}
          />
        ),
      },
      {
        title: "Ações",
        key: "actions",
        align: "right",
        width: canAcceptOnTab ? 360 : 110,
        render: (_, candidate) => {
          const isBusy = acceptingKey === candidate.rowKey;
          const showBidActions =
            canAcceptOnTab && freight.contractingType === "BID";
          return (
            <Box className="filas-hub-actions">
              <Tooltip title="Visualizar cadastro">
                <Button
                  component={Link}
                  to={`/newEmbarcador/${candidate.idCarrier}`}
                  state={{ viewOnly: true }}
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  sx={outlineButtonSx}
                >
                  Ver
                </Button>
              </Tooltip>
              {canAcceptOnTab && candidate.canAccept !== false ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={isBusy}
                  startIcon={
                    isBusy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckCircleOutlineIcon />
                    )
                  }
                  onClick={() => handleAcceptCandidate(freight, candidate)}
                  sx={acceptButtonSx}
                >
                  Aceitar
                </Button>
              ) : null}
              {showBidActions && candidate.canCounter ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isBusy}
                  startIcon={<SwapHorizOutlinedIcon />}
                  onClick={() => handleOpenCounter(freight, candidate)}
                  sx={outlineButtonSx}
                >
                  Contraproposta
                </Button>
              ) : null}
              {showBidActions && candidate.canReject ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isBusy}
                  onClick={() => handleOpenReject(freight, candidate)}
                  sx={rejectOutlineButtonSx}
                >
                  Recusar
                </Button>
              ) : null}
              {showBidActions &&
              !candidate.canAccept &&
              !candidate.canCounter &&
              candidate.negotiationTurn === "carrier" ? (
                <Typography variant="caption" color="text.secondary">
                  Aguardando resposta
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [acceptingKey, canAcceptOnTab],
  );

  const freightColumns = useMemo(
    () => {
      const showAcceptedCarrier =
        activeTab === "accepted" || activeTab === "finished";

      const columns = [
        {
          title: "Nº do frete",
          dataIndex: "numberSerial",
          key: "numberSerial",
          render: (value, record) => (
            <Box>
              <Typography className="filas-hub-freight-number">#{value}</Typography>
              <Typography variant="caption" color="text.secondary">
                {record.shipperName}
              </Typography>
            </Box>
          ),
        },
        {
          title: "Rota",
          key: "route",
          render: (_, record) => (
            <Typography className="filas-hub-route" variant="body2">
              {record.origin} → {record.destination}
            </Typography>
          ),
        },
        {
          title: "Coleta",
          dataIndex: "collectionDate",
          key: "collectionDate",
          width: 120,
          render: (value) => formatDateBr(value),
        },
        {
          title: "Melhor valor final",
          dataIndex: "freightValue",
          key: "freightValue",
          width: 180,
          render: (value, record) => {
            const hasBid =
              record.contractingType === "BID" && record.bestProposedAmount;
            const total = hasBid
              ? record.bestProposedAmount
              : record.freightTotal || value;
            const unit =
              record.pricingType === "Ton"
                ? hasBid
                  ? record.bestProposedUnitAmount
                  : record.pricePerTon
                : null;
            return <ShipperAmount total={total} unit={unit} />;
          },
        },
      ];

      if (showAcceptedCarrier) {
        columns.push({
          title: "Transportadora",
          key: "acceptedCarrier",
          width: 220,
          render: (_, record) => (
            <Box className="filas-hub-winner">
              <LocalShippingOutlinedIcon className="filas-hub-winner__icon" />
              <Box>
                <Typography className="filas-hub-winner__name">
                  {record.acceptedCarrierName || "—"}
                </Typography>
                {record.acceptedCarrier?.idCarrier ? (
                  <Typography variant="caption" color="text.secondary">
                    Vencedora da fila
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ),
        });
      } else {
        columns.push({
          title: "Propostas",
          dataIndex: "queueCount",
          key: "queueCount",
          width: 110,
          render: (value) => <ProposalCountBadge value={value} />,
        });
      }

      columns.push(
        {
          title: "Status",
          key: "status",
          width: 200,
          render: (_, record) => (
            <StatusBadge
              label={record.statusUiLabel}
              tone={resolveStatusTone(record.statusUiLabel, activeTab)}
            />
          ),
        },
        {
          title: "Ações",
          key: "actions",
          align: "right",
          width: 300,
          render: (_, record) => {
            const showCancel = canShowCancelForFreight(record, user);
            const isCancelingThisFreight =
              cancelFreightLoading &&
              pendingCancelFreight?.freightId === record.freightId;
            const isExpanded = expandedRowKeys.includes(record.freightId);
            const hasQueue = record.queueCount > 0;

            return (
              <Box className="filas-hub-freight-actions">
                {hasQueue ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ListAltOutlinedIcon />}
                    onClick={() => toggleExpandFreight(record.freightId)}
                    sx={outlineButtonSx}
                  >
                    {isExpanded ? "Ocultar" : "Ver propostas"}
                  </Button>
                ) : null}
                {showCancel ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      isCancelingThisFreight ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CancelOutlinedIcon />
                      )
                    }
                    disabled={cancelFreightLoading}
                    onClick={() => handleOpenCancelFreightDialog(record)}
                    sx={cancelButtonSx}
                  >
                    Cancelar
                  </Button>
                ) : null}
              </Box>
            );
          },
        },
      );

      return columns;
    },
    [activeTab, cancelFreightLoading, expandedRowKeys, pendingCancelFreight, user],
  );

  const summaryValue = (card) => {
    const value = summary?.[card.key];
    if (typeof value === "number") return value;
    if (card.legacyKey && typeof summary?.[card.legacyKey] === "number") {
      return summary[card.legacyKey];
    }
    return 0;
  };

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (!canView) {
    return <Navigate to="/" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <Box className="filas-hub-page">
          <Box className="filas-hub-page__header">
            <Typography variant="h5" className="filas-hub-page__title">
              Gestão de Lances
            </Typography>
            <Typography variant="body2" className="filas-hub-page__subtitle">
              Acompanhe e gerencie fretes com transportadoras na fila. Veja propostas em
              aberto, aceitas, finalizadas e canceladas.
            </Typography>
          </Box>

          <Box className="filas-hub-summary">
            {SUMMARY_CARDS.map((card) => {
              const Icon = card.Icon;
              return (
                <Box
                  key={card.key}
                  className={`filas-hub-summary__card filas-hub-summary__card--${card.tone}`}
                >
                  <Box className="filas-hub-summary__top">
                    <Typography className="filas-hub-summary__label">
                      {card.label}
                    </Typography>
                    <span
                      className={`filas-hub-summary__icon filas-hub-summary__icon--${card.tone}`}
                    >
                      <Icon fontSize="small" />
                    </span>
                  </Box>
                  <Typography className="filas-hub-summary__value">
                    {summaryValue(card)}
                  </Typography>
                  <Typography className="filas-hub-summary__hint">{card.hint}</Typography>
                </Box>
              );
            })}
          </Box>

          <Box className="filas-hub-tabs">
            <Tabs
              value={filters.tab || "open"}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {HUB_TABS.map((tab) => (
                <Tab key={tab.key} value={tab.key} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          <Box className="filas-hub-filters">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nº do frete"
                  value={filters.number}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, number: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={originOptions}
                  value={filters.origin || ""}
                  inputValue={filters.origin || ""}
                  onInputChange={(_, input, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setOriginOptions(buildCityOptions(input));
                      setFilters((prev) => ({
                        ...prev,
                        origin: reason === "clear" ? "" : input,
                      }));
                    }
                  }}
                  onChange={(_, value) => {
                    const next =
                      typeof value === "string"
                        ? value
                        : value == null
                          ? ""
                          : String(value);
                    setFilters((prev) => ({ ...prev, origin: next }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Origem"
                      placeholder="Ex: Belo Horizonte - MG"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={destinationOptions}
                  value={filters.destination || ""}
                  inputValue={filters.destination || ""}
                  onInputChange={(_, input, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setDestinationOptions(buildCityOptions(input));
                      setFilters((prev) => ({
                        ...prev,
                        destination: reason === "clear" ? "" : input,
                      }));
                    }
                  }}
                  onChange={(_, value) => {
                    const next =
                      typeof value === "string"
                        ? value
                        : value == null
                          ? ""
                          : String(value);
                    setFilters((prev) => ({ ...prev, destination: next }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Destino"
                      placeholder="Ex: São Paulo - SP"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filas-hub-shipper-label">Embarcador</InputLabel>
                  <Select
                    labelId="filas-hub-shipper-label"
                    label="Embarcador"
                    value={filters.uidShipperFilter || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        uidShipperFilter: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {shippers.map((shipper) => (
                      <MenuItem key={shipper.uid} value={shipper.uid}>
                        {shipper.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box className="filas-hub-filters__actions">
              <Button
                variant="text"
                onClick={handleClear}
                disabled={loading}
                sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600 }}
              >
                Limpar
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={loadHub}
                disabled={loading}
                sx={outlineButtonSx}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={acceptButtonSx}
              >
                Buscar
              </Button>
            </Box>
          </Box>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box className="filas-hub-table-wrap">
            <Table
              rowKey="freightId"
              columns={freightColumns}
              dataSource={freightGroups}
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Mostrando ${range[0]} a ${range[1]} de ${paginationTotal || total}`,
              }}
              locale={{
                emptyText: (
                  <Box className="filas-hub-empty">
                    {EMPTY_TEXT_BY_TAB[activeTab] || EMPTY_TEXT_BY_TAB.open}
                  </Box>
                ),
              }}
              scroll={{ x: 1100 }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                rowExpandable: (record) => record.queueCount > 0,
                expandedRowRender: (freight) => (
                  <Box className="filas-hub-nested-wrap">
                    <Table
                      rowKey="rowKey"
                      columns={buildCandidateColumns(freight)}
                      dataSource={freight.queue}
                      pagination={false}
                      size="small"
                      locale={{
                        emptyText: "Nenhuma proposta nesta fila.",
                      }}
                    />
                  </Box>
                ),
              }}
            />
          </Box>

          <Dialog
            open={Boolean(confirmAccept)}
            onClose={() => !acceptingKey && setConfirmAccept(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar aceite da transportadora</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Atribuir o frete <strong>#{confirmAccept?.numberSerial}</strong>{" "}
                  ({confirmAccept?.origin} → {confirmAccept?.destination}) à
                  transportadora{" "}
                  <strong>{confirmAccept?.nameCarrier}</strong>
                  {confirmAccept?.proposedAmount
                    ? ` por ${formatProposalLabel(
                        confirmAccept.proposedAmount,
                        confirmAccept.pricingType === "Ton"
                          ? confirmAccept.unitAmount
                          : null,
                      )}`
                    : ""}
                  ?
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setConfirmAccept(null)}
                disabled={Boolean(acceptingKey)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmAccept}
                disabled={Boolean(acceptingKey)}
                sx={acceptButtonSx}
              >
                {acceptingKey ? "Aceitando..." : "Confirmar aceite"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(confirmCounter)}
            onClose={() => !acceptingKey && setConfirmCounter(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Enviar contraproposta</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Uma única contraproposta para{" "}
                  <strong>{confirmCounter?.nameCarrier}</strong> no frete{" "}
                  <strong>#{confirmCounter?.numberSerial}</strong>. Depois disso
                  a transportadora pode aceitar ou enviar uma tréplica.
                </Typography>
                {confirmCounter?.proposedAmount ? (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Lance atual:{" "}
                    <strong>
                      {formatProposalLabel(
                        confirmCounter.proposedAmount,
                        confirmCounter.pricingType === "Ton"
                          ? confirmCounter.unitAmount
                          : null,
                      )}
                    </strong>
                  </Typography>
                ) : null}
                <IntlCurrencyInput
                  currency="BRL"
                  config={currencyConfig}
                  value={counterAmount || 0}
                  disabled={Boolean(acceptingKey)}
                  onChange={(_, value) => setCounterAmount(value)}
                  className="form-control filas-hub-counter-input"
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setConfirmCounter(null)}
                disabled={Boolean(acceptingKey)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmCounter}
                disabled={Boolean(acceptingKey)}
                sx={acceptButtonSx}
              >
                {acceptingKey ? "Enviando..." : "Enviar contraproposta"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(confirmReject)}
            onClose={() => !acceptingKey && setConfirmReject(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Recusar lance</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2">
                  Recusar o fio de <strong>{confirmReject?.nameCarrier}</strong>?
                  Este prestador sai do frete e não poderá enviar outro lance.
                  O frete continua aberto para os demais.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setConfirmReject(null)}
                disabled={Boolean(acceptingKey)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmReject}
                disabled={Boolean(acceptingKey)}
                sx={cancelButtonSx}
              >
                {acceptingKey ? "Recusando..." : "Recusar lance"}
              </Button>
            </DialogActions>
          </Dialog>

          <CancelFreightDialog
            open={openCancelFreightDialog}
            loading={cancelFreightLoading}
            onClose={handleCloseCancelFreightDialog}
            onConfirm={handleConfirmCancelFreight}
            message="O frete será cancelado e sairá da lista de disponíveis. Transportadoras na fila serão notificadas."
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
}
